import { useEffect, useState, type SubmitEventHandler } from 'react';

import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import { Notification } from '@eneco/components-next/Notification/Notification.js';
import {
  postEnecoBeXapiSiteApiV1RagClassifyIntent,
  postEnecoBeXapiSiteApiV1RagRagAnswer,
  postEnecoBeXapiSiteApiV1RagStoreCsat,
} from '@eneco/dc-be/client';
import {
  Box,
  Button,
  ButtonLink,
  Card,
  Form,
  Grid,
  Heading,
  Image,
  Stack,
  Stretch,
  Text,
  TextArea,
} from '@eneco/sparky';
import { EnvelopeIcon, SendIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { ContactFormDeflectionRendering } from '@sitecore/types/ContactFormDeflection';

import { LoadingListSpinner } from '../../components/Loading/LoadingListSpinner';
import { useSetNotificationAndScrollToTop } from '../../hooks/useSetNotificationAndScrollToTop';
import { inlineParagraphReplacement } from '../../utils/richTextReplacements';
import { CONTACT_FORM_DEFLECTION_QUESTION_KEY } from '../contact-form/ContactForm';

const StyledCsatButton = styled('button', {
  backgroundColor: 'white',
  borderRadius: '$s',
  padding: '$3',
  paddingTop: '$4',
  width: '100%',
  height: '100%',
  display: 'flex',
  gap: '$4',
  textAlign: 'left',
  boxShadow: '$s',
  cursor: 'pointer',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  variants: {
    active: {
      true: {},
      false: {},
    },
    color: {
      red: {
        '& > div': {
          backgroundColor: '$enecoRed700',
          color: '$textInverted',
        },
      },
      orange: {
        '& > div': {
          backgroundColor: '$orange400',
          color: '$text',
        },
      },
      yellow: {
        '& > div': {
          backgroundColor: '$ochre_300',
          color: '$text',
        },
      },
      green: {
        '& > div': {
          backgroundColor: '$accentGreen300',
          color: '$text',
        },
      },
      darkGreen: {
        '& > div': {
          backgroundColor: '$accentGreen600',
          color: '$textInverted',
        },
      },
    },
  },
  defaultVariants: {
    active: false,
  },
  compoundVariants: [
    { active: true, color: 'red', css: { borderColor: '$enecoRed700' } },
    { active: true, color: 'orange', css: { borderColor: '$orange400' } },
    { active: true, color: 'yellow', css: { borderColor: '$ochre_300' } },
    { active: true, color: 'green', css: { borderColor: '$accentGreen300' } },
    { active: true, color: 'darkGreen', css: { borderColor: '$accentGreen600' } },
  ],
});

const StyledCsatNumber = styled('div', {
  fontSize: '$bodyXL',
  fontWeight: 'bold',
  display: 'flex',
  flexShrink: 0,
  borderRadius: '$round',
  width: '24px',
  height: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-$1',
  '& > span': {
    paddingTop: '2px',
    display: 'block',
  },
});

const StyledMarginLeftButton = styled(ButtonLink, {
  '@md': {
    marginLeft: 'auto',
  },
});

const StyledForm = styled(Form, {
  display: 'contents',
});

const CSAT_OPTIONS = [
  { number: 1, color: 'red' as const, contentKey: 'csatVeryUnhappyContent' },
  { number: 2, color: 'orange' as const, contentKey: 'csatUnhappyContent' },
  { number: 3, color: 'yellow' as const, contentKey: 'csatNeutralContent' },
  { number: 4, color: 'green' as const, contentKey: 'csatHappyContent' },
  { number: 5, color: 'darkGreen' as const, contentKey: 'csatVeryHappyContent' },
] as const;

const ContactFormDeflection = () => {
  const { fields } = useContent<ContactFormDeflectionRendering>();
  const { language } = useApplication();
  const setNotification = useSetNotificationAndScrollToTop();
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [csatRating, setCsatRating] = useState<number | null>(null);
  const [isLoadingCsat, setIsLoadingCsat] = useState(false);
  const [csatFeedback, setCsatFeedback] = useState<string | null>(null);
  const [intent, setIntent] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const { trackEventBE } = useTracking();

  const handleCsatClick = (rating: number) => {
    setCsatRating(rating);
  };

  const onSubmit: SubmitEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    setAnswer(null);
    setIntent(null);
    setIsLoadingAnswer(true);
    setCsatRating(null);
    setCsatFeedback(null);
    setNotification(null);
    setValidationError(undefined);
    const formData = new FormData(e.currentTarget);
    const question = formData.get('question');
    trackEventBE('cf_question_submit', { data: { flow_type: 'deflection', question_filled: !!question } });

    if (!question) {
      setIsLoadingAnswer(false);
      setValidationError(fields.data.inputFormField.value.requiredMessage);
      return;
    }
    const questionStr = question as string;
    localStorage.setItem(CONTACT_FORM_DEFLECTION_QUESTION_KEY, questionStr);
    try {
      const [classifyResult, ragResult] = await Promise.all([
        postEnecoBeXapiSiteApiV1RagClassifyIntent({ body: { message: questionStr, language } }),
        postEnecoBeXapiSiteApiV1RagRagAnswer({
          body: {
            date: new Date().toISOString(),
            question: questionStr,
            language,
          },
        }),
      ]);
      if (classifyResult.response.ok && classifyResult.data?.data?.category != null) {
        setIntent(classifyResult.data.data.category);
      }
      const { response, data } = ragResult;
      if (!response.ok) {
        return setNotification({
          notificationOptions: {
            title: fields.data.generalErrorNotification.value.title,
            text: <RichText html={fields.data.generalErrorNotification.value.content} />,
            variant: fields.data.generalErrorNotification.value.variant,
          },
          shouldScroll: true,
        });
      }
      setAnswer(data?.data?.answer || null);
    } catch {
      return setNotification({
        notificationOptions: {
          title: fields.data.generalErrorNotification.value.title,
          text: <RichText html={fields.data.generalErrorNotification.value.content} />,
          variant: fields.data.generalErrorNotification.value.variant,
        },
        shouldScroll: true,
      });
    } finally {
      setIsLoadingAnswer(false);
    }
  };

  const handleCsatSubmit: SubmitEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    setIsLoadingCsat(true);
    setNotification(null);
    const formData = new FormData(e.currentTarget);
    const csatFeedback = formData.get('csatFeedback');
    if (!csatFeedback) {
      return;
    }
    try {
      const { response } = await postEnecoBeXapiSiteApiV1RagStoreCsat({
        body: {
          csatFeedback: csatFeedback as string,
          csatScore: csatRating,
          date: new Date().toISOString(),
          intentValue: intent ?? undefined,
          language,
        },
      });
      if (!response.ok) {
        return setCsatFeedback(fields.csatRate.csatErrorNotification.value.title);
      }
      setCsatFeedback(fields.csatRate.csatSuccessNotification.value.title);
    } catch {
      return setCsatFeedback(fields.csatRate.csatErrorNotification.value.title);
    } finally {
      setIsLoadingCsat(false);
      setCsatRating(null);
    }
  };

  const handleRetryQuestionClick = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    trackEventBE('cf_retry_question', { data: { flow_type: 'deflection' } });
    resetForm();
  };

  const resetForm = () => {
    setAnswer(null);
    setIntent(null);
    setCsatRating(null);
    setCsatFeedback(null);
    setIsLoadingAnswer(false);
    setIsLoadingCsat(false);
  };

  const handleClearAnswerClick = () => {
    trackEventBE('cf_answer_clear', { data: { flow_type: 'deflection' } });
  };

  const handleSendEmailClick = () => {
    trackEventBE('cf_click_email_cta', { data: { flow_type: 'deflection' } });
  };

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      trackEventBE('cf_page_close', { data: { flow_type: 'deflection' } });
    });
    return () => {
      window.removeEventListener('beforeunload', () => {
        trackEventBE('cf_page_close', { data: { flow_type: 'deflection' } });
      });
    };
  }, [trackEventBE]);

  return (
    <Box paddingX={{ initial: '0', lg: '32' }}>
      <Notification />
      <Card>
        <Box padding="6">
          <Stack gap="6" alignX="start">
            <StyledForm onSubmit={onSubmit}>
              <Stretch>
                <Stack gap="4">
                  <Heading size="S" as="h2">
                    {fields.data.questionTitle.value}
                  </Heading>
                  <TextArea
                    name="question"
                    label={fields.data.inputFormField.value.label}
                    hint={<RichText html={fields.data.inputFormField.value.hint} />}
                    placeholder={fields.data.inputFormField.value.placeholder}
                    rows={6}
                    maxLength={2000}
                    error={validationError}
                    onChange={e =>
                      setValidationError(!e.target.value ? fields.data.inputFormField.value.requiredMessage : undefined)
                    }
                  />
                </Stack>
              </Stretch>
              {!answer && !isLoadingAnswer && (
                <Button type="submit">
                  <SendIcon />
                  {fields.data.submitQuestionButtonText.value}
                </Button>
              )}
            </StyledForm>
            {(answer || isLoadingAnswer) && (
              <>
                {!answer && (
                  <Heading size="3XS" as="h2">
                    {fields.data.answerTitle.value}
                  </Heading>
                )}
                <Stretch>
                  <Box borderRadius="s" backgroundColor="backgroundSecondary" paddingX="6" paddingY="10">
                    {isLoadingAnswer ? <LoadingListSpinner /> : null}
                    {answer ? (
                      <Grid columns="2" gridTemplateColumns={'40px 1fr'} gap="4">
                        <Grid.Item>
                          <Image
                            src={fields.data.answerImage?.value?.formats[0].src}
                            alt={fields.data.answerImage?.value?.alt || 'Answer image'}
                            width="40px"
                            height="40px"
                            objectFit="cover"
                            display="block"
                          />
                        </Grid.Item>

                        <Grid.Item gridColumn={'2 / -1'}>
                          <RichText html={answer} replacements={inlineParagraphReplacement} />
                        </Grid.Item>
                        <Grid.Item gridColumn={'2 / -1'}>
                          <Box paddingTop="8">
                            <Text size="BodyS" color="textLowEmphasis">
                              <RichText html={fields.data.disclaimerContent?.value} />
                            </Text>
                          </Box>
                        </Grid.Item>
                      </Grid>
                    ) : null}
                  </Box>
                </Stretch>
                {answer && (
                  <Stretch>
                    <Box padding="6" backgroundColor="backgroundSecondary" borderRadius="s">
                      <Stack gap="4" alignX="start">
                        <Heading size="3XS" as="h2">
                          {fields.csatRate.csatTitle?.value}
                        </Heading>
                        {isLoadingCsat ? (
                          <Stretch>
                            <Stack alignX="center" alignY="center">
                              <LoadingListSpinner />
                            </Stack>
                          </Stretch>
                        ) : csatFeedback ? (
                          <RichText html={csatFeedback} />
                        ) : (
                          <Stretch>
                            <Stack direction={{ initial: 'column', lg: 'row' }} gap="4">
                              {CSAT_OPTIONS.map(({ number, color, contentKey }) => (
                                <Stack.Item key={number} grow>
                                  <StyledCsatButton
                                    color={color}
                                    type="button"
                                    onClick={() => handleCsatClick(number)}
                                    active={csatRating === number}>
                                    <StyledCsatNumber>
                                      <span>{number}</span>
                                    </StyledCsatNumber>
                                    <RichText html={fields.csatRate[contentKey]?.value} />
                                  </StyledCsatButton>
                                </Stack.Item>
                              ))}
                            </Stack>
                          </Stretch>
                        )}
                        {csatRating && !isLoadingCsat && (
                          <StyledForm onSubmit={handleCsatSubmit}>
                            <Stretch>
                              <Stack.Item>
                                <TextArea
                                  name="csatFeedback"
                                  label={fields.csatRate.csatFeedbackFormField.value.label}
                                  hint={<RichText html={fields.csatRate.csatFeedbackFormField.value.hint} />}
                                  placeholder={fields.csatRate.csatFeedbackFormField.value.placeholder}
                                  rows={2}
                                />
                              </Stack.Item>
                            </Stretch>
                            <Button type="submit" size="compact">
                              {fields.csatRate.csatSubmitButtonText.value}
                            </Button>
                          </StyledForm>
                        )}
                      </Stack>
                    </Box>
                    <Stack direction={{ initial: 'column', md: 'row' }} gap="4" wrap>
                      <Button action="secondary" onClick={handleRetryQuestionClick}>
                        {fields.data.askQuestionButtonText.value}
                      </Button>
                      <StyledMarginLeftButton
                        onClick={handleClearAnswerClick}
                        href={fields.data.answerIsClearButtonLink.value.href}>
                        {fields.data.answerIsClearButtonLink.value.text}
                      </StyledMarginLeftButton>
                      <ButtonLink
                        onClick={handleSendEmailClick}
                        href={`${fields.data.sendAnEmailButtonLink.value.href}?subject=${intent}`}>
                        <EnvelopeIcon />
                        {fields.data.sendAnEmailButtonLink.value.text}
                      </ButtonLink>
                    </Stack>
                  </Stretch>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};

export default ContactFormDeflection;
